// NorthIndianChart.jsx — real North Indian ("diamond") D1 chart geometry,
// shared by the Astro Vastu editor screen (SVG) and both PDF export paths
// (jsPDF vector drawing). Previously the D1 chart was just a plain 4x4 grid
// of boxes on screen and a plain table in the PDF — neither is how an actual
// Kundli chart looks. This is the real thing.
//
// Construction (the standard used by every North Indian chart generator):
// take a square, draw both corner-to-corner diagonals, then draw a second,
// smaller diamond by connecting the midpoints of the four sides. That single
// square + 2 diagonals + 1 diamond produces exactly 12 regions:
//   - 4 kite shapes centered on the N/E/S/W edges — these are always
//     Houses 1 / 4 / 7 / 10 (the "Kendras"), Lagna (House 1) fixed at the top.
//   - 8 corner triangles (2 per corner) — Houses 2,3 / 5,6 / 8,9 / 11,12.
// Numbering runs anticlockwise from the top (see HOUSE_POLYGONS below), regardless of which Rashi
// the Ascendant actually is — only the Rashi *names* written inside change
// per chart, never the house-to-position layout. Verified against a working
// reference SVG generator (github.com/rahulrai89, "north indian vedic
// astrology chart svgwrite" gist) before deriving the clean 0–1 fractional
// coordinates below.
//
// All coordinates are fractions of a unit square (0..1), scale-independent —
// multiply by any pixel/mm size for SVG or jsPDF.

const TL = [0, 0], TR = [1, 0], BR = [1, 1], BL = [0, 1];
const M_T = [0.5, 0], M_R = [1, 0.5], M_B = [0.5, 1], M_L = [0, 0.5];
const C = [0.5, 0.5];
const Q1 = [0.75, 0.25]; // top-right quarter point
const Q2 = [0.75, 0.75]; // bottom-right quarter point
const Q3 = [0.25, 0.75]; // bottom-left quarter point
const Q4 = [0.25, 0.25]; // top-left quarter point

const HOUSE_POLYGONS_CLOCKWISE = {
  1:  [M_T, Q1, C, Q4],   // North kite   — Lagna
  2:  [M_T, Q1, TR],
  3:  [Q1, M_R, TR],
  4:  [Q1, C, Q2, M_R],   // East kite
  5:  [Q2, BR, M_R],
  6:  [Q2, M_B, BR],
  7:  [Q3, M_B, Q2, C],   // South kite
  8:  [Q3, BL, M_B],
  9:  [M_L, BL, Q3],
  10: [M_L, Q3, C, Q4],   // West kite
  11: [TL, M_L, Q4],
  12: [TL, Q4, M_T],
};
// Houses run ANTICLOCKWISE from the top kite (House 1 top, House 2 top-left,
// House 4 the left kite, House 7 bottom, House 10 the right kite) — the way
// ProKerala and every standard North Indian chart draws them. The table
// above was laid out clockwise, which mirrored the whole chart left-to-right
// (e.g. House 2's planets showed top-right instead of top-left). Flipping x
// fixes it for the on-screen SVG and both PDF exports at once, since they all
// read HOUSE_POLYGONS; GRID_LINES is symmetric so it needs no change.
export const HOUSE_POLYGONS = Object.fromEntries(
  Object.entries(HOUSE_POLYGONS_CLOCKWISE).map(([house, pts]) => [house, pts.map(([x, y]) => [1 - x, y])])
);

// The outer square + both diagonals + inner diamond — the lines every house
// polygon's edges trace over. Drawn once, underneath the polygons, so shared
// edges read as a single crisp line instead of two overlapping strokes.
export const GRID_LINES = [
  [TL, TR], [TR, BR], [BR, BL], [BL, TL], // outer square
  [TL, BR], [TR, BL],                     // diagonals
  [M_T, M_R], [M_R, M_B], [M_B, M_L], [M_L, M_T], // inner diamond
];

// 2-letter Western zodiac codes, indexed by Rashi number (1=Mesh/Aries..12=Meen/Pisces)
// — short enough to fit the small corner-triangle houses, and unambiguous
// against the planet codes below (unlike the Hindi-name first letters, which
// collide: Mesh/Meen both start "Me", same as Mercury).
export const RASHI_SHORT = { 1:'Ar', 2:'Ta', 3:'Ge', 4:'Cn', 5:'Le', 6:'Vi', 7:'Li', 8:'Sc', 9:'Sg', 10:'Cp', 11:'Aq', 12:'Pi' };

export const GRAHA_SHORT = { Sun:'Su', Moon:'Mo', Mars:'Ma', Mercury:'Me', Jupiter:'Ju', Venus:'Ve', Saturn:'Sa', Rahu:'Ra', Ketu:'Ke' };

// Simple vertex-average centroid — good enough for these convex shapes, and
// for the 4 kite houses it lands away from the shared center vertex (so text
// from adjacent kites doesn't collide), which is exactly what we want.
const centroid = (pts) => {
  const n = pts.length;
  const sx = pts.reduce((s, p) => s + p[0], 0);
  const sy = pts.reduce((s, p) => s + p[1], 0);
  return [sx / n, sy / n];
};

// ── SVG component — used in the editor screen ──────────────────────────────
// `houseLabels` (optional, { [houseNum]: 'label text' }) overrides the
// Rashi short-code line with any other per-house text — same geometry,
// same planets line, just a different middle label. Used for the Vedic
// Vastu / AstroVastu / Laal Kitaab Makaan chart variants (see
// VastuChartVariants.js) so all four charts share one implementation.
export function NorthIndianChartSVG({ lagnaRashi, houses = {}, selectedHouse, onSelectHouse, getRashi, size = 320, houseLabels = null, labelFontSize = 11.5 }) {
  const S = 400;
  return (
    <svg viewBox={`0 0 ${S} ${S}`} width={size} height={size} style={{ display: 'block', flexShrink: 0 }}>
      {/* Fully monochrome by request — white cells, black lines/text
          throughout, no teal accents anywhere in the chart itself. The only
          visual cue for the selected house is a thicker black border.
          Fills are drawn first and the grid lines + outer border LAST, on
          top of everything — drawing lines first (underneath the white
          polygon fills) let each house's fill paint over the very lines
          that were meant to show through, so some edges rendered thin/solid
          and others got silently covered depending on browser rounding —
          "kuch lines dikh rahi, kuch gayab". Lines on top removes that
          entirely: every edge is the same explicit stroke, always visible. */}
      {Array.from({ length: 12 }, (_, i) => i + 1).map((houseNum) => {
        const pts = HOUSE_POLYGONS[houseNum].map(([fx, fy]) => [fx * S, fy * S]);
        const [cx, cy] = centroid(pts);
        const rashi = lagnaRashi ? getRashi(lagnaRashi, houseNum) : null;
        const label = houseLabels ? houseLabels[houseNum] : (rashi ? RASHI_SHORT[rashi.num] : null);
        // Every north-Indian chart variant (D1, Vedic Vastu, AstroVastu, Laal Kitaab) prints the SIGN
        // number (Aries=1 ... Pisces=12) like ProKerala, so the Lagna box shows its own sign's number.
        const houseNumberShown = rashi ? rashi.num : houseNum;
        const planets = houses[houseNum] || [];
        const isSel = selectedHouse === houseNum;
        const hasContent = planets.length > 0;
        return (
          <g key={houseNum} onClick={() => onSelectHouse && onSelectHouse(houseNum)} style={{ cursor: onSelectHouse ? 'pointer' : 'default' }}>
            <polygon
              points={pts.map((p) => p.join(',')).join(' ')}
              fill={isSel ? '#f1f5f9' : '#ffffff'}
            />
            {/* Live UI chart text bumped to 1.5x (house number, direction/
                rashi label, planet list) per explicit request — the PDF
                export version (drawNorthIndianChartPdf below) is untouched. */}
            <text x={cx} y={cy - 11} textAnchor="middle" fontSize="14.25" fontWeight="700" fill="#000000" style={{ pointerEvents: 'none' }}>{houseNumberShown}</text>
            {label && (
              <text x={cx} y={cy + 3} textAnchor="middle" fontSize={labelFontSize} fontWeight="700" fill="#000000" style={{ pointerEvents: 'none' }}>
                {label}
              </text>
            )}
            {hasContent && (
              <text x={cx} y={cy + 17} textAnchor="middle" fontSize="12" fontWeight="600" fill="#000000" style={{ pointerEvents: 'none' }}>
                {planets.map((p) => GRAHA_SHORT[p] || p).join(' ')}
              </text>
            )}
          </g>
        );
      })}
      <g style={{ pointerEvents: 'none' }}>
        {GRID_LINES.map(([a, b], i) => (
          <line key={i} x1={a[0] * S} y1={a[1] * S} x2={b[0] * S} y2={b[1] * S} stroke="#000000" strokeWidth="1.25" strokeLinecap="square" />
        ))}
        {selectedHouse && (
          <polygon
            points={HOUSE_POLYGONS[selectedHouse].map(([fx, fy]) => [fx * S, fy * S].join(',')).join(' ')}
            fill="none" stroke="#000000" strokeWidth="2.5"
          />
        )}
        <rect x="0" y="0" width={S} height={S} fill="none" stroke="#000000" strokeWidth="1.75" />
      </g>
    </svg>
  );
}

// ── South Indian ("box") chart — fixed-Rashi variant of the same 4 charts ──
// Unlike the North Indian diamond above (house 1 always fixed at the top,
// Rashi names rotate around it per the Ascendant), the South Indian style
// fixes every Rashi to its own permanent cell in a 4x4 grid (the 4 centre
// cells stay empty) and instead moves the HOUSE NUMBERS around depending on
// the Ascendant — Aries always sits in the top row's 2nd cell, then Taurus,
// Gemini, ... continue clockwise around the border. Reference layout
// (confirmed against the standard South Indian Rasi chart convention):
//   Pisces | Aries | Taurus | Gemini
//   Aquarius|      |        | Cancer
//   Capricorn|     |        | Leo
//   Sagittarius|Scorpio|Libra| Virgo
const SOUTH_INDIAN_POSITIONS = {
  1: [0, 1], 2: [0, 2], 3: [0, 3],   // Aries, Taurus, Gemini
  4: [1, 3], 5: [2, 3],              // Cancer, Leo
  6: [3, 3], 7: [3, 2], 8: [3, 1], 9: [3, 0], // Virgo, Libra, Scorpio, Sagittarius
  10: [2, 0], 11: [1, 0],            // Capricorn, Aquarius
  12: [0, 0],                        // Pisces
};
// Reading order around the loop (1..12 already IS the clockwise physical
// order given the fixed positions above) — used to draw the small arrows
// showing the fixed Rashi progression, same idea as the reference chart.
const SOUTH_LOOP_ORDER = [1,2,3,4,5,6,7,8,9,10,11,12];

// `houseLabels`/`getRashi` play the same role as in NorthIndianChartSVG —
// shared by the same 4 chart variants (plain D1, Vedic Vastu, AstroVastu,
// Laal Kitaab). Since Rashi position is fixed here (not house position),
// `getRashi` is only needed in reverse: given a fixed cell's Rashi and the
// Ascendant, which house number does that cell currently represent.
export function SouthIndianChartSVG({ lagnaRashi, houses = {}, selectedHouse, onSelectHouse, size = 320, houseLabels = null, labelFontSize = 11.5 }) {
  const S = 400;
  const CELL = S / 4;
  const cellRect = (row, col) => ({ x: col * CELL, y: row * CELL });
  // Fixed Rashi cell → current house number, given where the Ascendant sits.
  // Inverse of getRashi(lagnaRashi, houseNum) = RASHIS[(lagnaRashi-1+houseNum-1)%12].
  const houseForRashi = (rashiNum) => lagnaRashi ? (((rashiNum - lagnaRashi + 12) % 12) + 1) : null;

  const ARROW = 7; // half-size of each loop arrowhead, in the 400-unit viewBox
  const TAIL  = 12; // length of the shaft drawn behind the arrowhead
  const DIR_VEC = { right: [1, 0], left: [-1, 0], down: [0, 1], up: [0, -1] };
  const arrowPoints = (cx, cy, dir) => {
    switch (dir) {
      case 'right': return `${cx - ARROW},${cy - ARROW} ${cx - ARROW},${cy + ARROW} ${cx + ARROW},${cy}`;
      case 'left':  return `${cx + ARROW},${cy - ARROW} ${cx + ARROW},${cy + ARROW} ${cx - ARROW},${cy}`;
      case 'down':  return `${cx - ARROW},${cy - ARROW} ${cx + ARROW},${cy - ARROW} ${cx},${cy + ARROW}`;
      default:      return `${cx - ARROW},${cy + ARROW} ${cx + ARROW},${cy + ARROW} ${cx},${cy - ARROW}`; // 'up'
    }
  };
  // Whole assembly (tail + arrowhead together) is centred ON the shared
  // border line between the two cells — half the total length sits behind
  // the centre point, half sits ahead of it (the head, being the "front"
  // half, ends up centred a bit forward of the line and the tail a bit
  // behind, rather than the tail trailing off lopsided to one side only).
  const arrowAssembly = (cx, cy, dir) => {
    const [dx, dy] = DIR_VEC[dir];
    const headCx = cx + dx * (TAIL / 2), headCy = cy + dy * (TAIL / 2);
    const half = TAIL / 2 + ARROW;
    return {
      headCx, headCy,
      tail: {
        x1: cx - dx * half, y1: cy - dy * half,
        x2: headCx - dx * (ARROW - 2), y2: headCy - dy * (ARROW - 2),
      },
    };
  };

  return (
    <svg viewBox={`0 0 ${S} ${S}`} width={size} height={size} style={{ display: 'block', flexShrink: 0 }}>
      {/* Same monochrome treatment as the North Indian chart — white cells,
          black lines/text, thicker border only on the selected house. */}
      {SOUTH_LOOP_ORDER.map((rashiNum) => {
        const [row, col] = SOUTH_INDIAN_POSITIONS[rashiNum];
        const { x, y } = cellRect(row, col);
        const cx = x + CELL / 2, cy = y + CELL / 2;
        const houseNum = houseForRashi(rashiNum);
        const label = houseLabels ? (houseNum ? houseLabels[houseNum] : null) : RASHI_SHORT[rashiNum];
        const planets = houseNum ? (houses[houseNum] || []) : [];
        const isSel = houseNum != null && selectedHouse === houseNum;
        const isLagna = houseNum === 1;
        return (
          <g key={rashiNum} onClick={() => houseNum && onSelectHouse && onSelectHouse(houseNum)} style={{ cursor: houseNum && onSelectHouse ? 'pointer' : 'default' }}>
            <rect x={x} y={y} width={CELL} height={CELL} fill={isSel ? '#f1f5f9' : '#ffffff'} stroke="#000000" strokeWidth="1.25" />
            {/* Rashi's own fixed short-code always shows (top-left corner) —
                the one label that never moves regardless of Ascendant,
                exactly like the fixed Rashi names printed on a real South
                Indian chart. */}
            <text x={x + 6} y={y + 16} fontSize="10.5" fontWeight="700" fill="#6b7280" style={{ pointerEvents: 'none' }}>{RASHI_SHORT[rashiNum]}</text>
            {/* Lagna marker — a small diagonal in the top-right corner, the
                traditional South Indian "this cell is the Ascendant" mark
                (houses don't get their own printed numbers on this style,
                since the Rashi cell itself is what's fixed). */}
            {isLagna && (
              <line x1={x + CELL - 22} y1={y + 2} x2={x + CELL - 2} y2={y + 22} stroke="#000000" strokeWidth="1.5" style={{ pointerEvents: 'none' }} />
            )}
            {/* Rashi number (Aries 1 ... Pisces 12), bottom-right — the SAME numbering the North Indian
                chart prints in each house, so the two charts of one person read identically. */}
            <text x={x + CELL - 7} y={y + CELL - 8} textAnchor="end" fontSize="13" fontWeight="800" fill="#374151" style={{ pointerEvents: 'none' }}>{rashiNum}</text>
            {label && (
              <text x={cx} y={cy + 4} textAnchor="middle" fontSize={labelFontSize} fontWeight="700" fill="#000000" style={{ pointerEvents: 'none' }}>
                {label}
              </text>
            )}
            {planets.length > 0 && (
              <text x={cx} y={cy + 20} textAnchor="middle" fontSize="12" fontWeight="600" fill="#000000" style={{ pointerEvents: 'none' }}>
                {planets.map((p) => GRAHA_SHORT[p] || p).join(' ')}
              </text>
            )}
          </g>
        );
      })}

      {/* Loop arrows — same decorative cue as the reference chart, showing
          the fixed clockwise Rashi progression around the border. */}
      <g style={{ pointerEvents: 'none' }}>
        {SOUTH_LOOP_ORDER.map((rashiNum, i) => {
          const nextRashi = SOUTH_LOOP_ORDER[(i + 1) % SOUTH_LOOP_ORDER.length];
          const [r1, c1] = SOUTH_INDIAN_POSITIONS[rashiNum];
          const [r2, c2] = SOUTH_INDIAN_POSITIONS[nextRashi];
          const p1 = cellRect(r1, c1), p2 = cellRect(r2, c2);
          const mx = (p1.x + p2.x) / 2 + CELL / 2, my = (p1.y + p2.y) / 2 + CELL / 2;
          const dir = r1 === r2 ? (c2 > c1 ? 'right' : 'left') : (r2 > r1 ? 'down' : 'up');
          const { headCx, headCy, tail } = arrowAssembly(mx, my, dir);
          return (
            <g key={rashiNum}>
              <line x1={tail.x1} y1={tail.y1} x2={tail.x2} y2={tail.y2} stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" />
              <polygon points={arrowPoints(headCx, headCy, dir)} fill="#9ca3af" />
            </g>
          );
        })}
      </g>

      <g style={{ pointerEvents: 'none' }}>
        {selectedHouse != null && SOUTH_LOOP_ORDER.map((rashiNum) => {
          if (houseForRashi(rashiNum) !== selectedHouse) return null;
          const [row, col] = SOUTH_INDIAN_POSITIONS[rashiNum];
          const { x, y } = cellRect(row, col);
          return <rect key={rashiNum} x={x} y={y} width={CELL} height={CELL} fill="none" stroke="#000000" strokeWidth="2.5" />;
        })}
        <rect x="0" y="0" width={S} height={S} fill="none" stroke="#000000" strokeWidth="1.75" />
      </g>
    </svg>
  );
}

// ── jsPDF vector drawer — used by both PDF export paths ────────────────────
// Draws the same chart as crisp vector lines/text directly onto a jsPDF
// document (no rasterization), at position (x,y) with the given side length
// `size` (mm). `getRashi` is the app's existing (lagnaRashi, houseNum) → rashi
// helper — passed in rather than imported, so this file stays framework/
// astrology-logic agnostic and reusable.
// `drawText` (optional) — a Devanagari-safe text drawer with the signature
// (text, x, y, fontSizePt, fontStyle, rgb, align) => void, e.g. this app's
// own drawLineSmart/drawLineSmartPdf. Only the houseLabels text itself
// needs it (once a non-English report language translates a direction/wall
// label into a non-Latin script — see compassCodeLabel/laalKitaabLabel in
// KundaliReportsData.js) — the house number and GRAHA_SHORT/RASHI_SHORT
// codes are always plain Latin/digits, so they stay on the caller's own
// jsPDF font via the plain fallback below when no drawText is given.
const defaultDrawText = (doc) => (text, tx, ty, fontSizePt, fontStyle, rgb, align) => {
  doc.setFont('helvetica', fontStyle || 'normal'); doc.setFontSize(fontSizePt); doc.setTextColor(...rgb);
  doc.text(text, tx, ty, align === 'center' ? { align: 'center' } : undefined);
};

export function drawNorthIndianChartPdf(doc, { x, y, size, lagnaRashi, houses = {}, getRashi, houseLabels = null, labelFontSize = 8.5, drawText = null }) {
  const S = size;
  const drawLabel = drawText || defaultDrawText(doc);
  // Fully monochrome by request — white cells, black lines/text throughout,
  // matching the editor screen (no teal tint on filled houses any more).
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.25);
  GRID_LINES.forEach(([a, b]) => {
    doc.line(x + a[0] * S, y + a[1] * S, x + b[0] * S, y + b[1] * S);
  });

  Array.from({ length: 12 }, (_, i) => i + 1).forEach((houseNum) => {
    const pts = HOUSE_POLYGONS[houseNum].map(([fx, fy]) => [x + fx * S, y + fy * S]);
    const [cx, cy] = centroid(pts);
    const rashi = lagnaRashi ? getRashi(lagnaRashi, houseNum) : null;
    const planets = houses[houseNum] || [];

    // PDF chart text bumped to 1.5x (house number, direction/rashi label,
    // planet list) to match the live-UI version (NorthIndianChartSVG
    // above) — was left at its original size when the live UI was bumped,
    // per explicit follow-up request to make the export match.
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10.5); doc.setTextColor(0, 0, 0);
    doc.text(String(rashi ? rashi.num : houseNum), cx, cy - 3.6, { align: 'center' });

    const label = houseLabels ? houseLabels[houseNum] : (rashi ? RASHI_SHORT[rashi.num] : null);
    if (label) {
      // Compound direction labels ("ESE & SE") were being squeezed onto one
      // line at a near-unreadable size to fit — wrap them onto 2 lines at
      // the " & " instead, so the font itself can stay legible.
      if (String(label).includes(' & ')) {
        const [l1, l2] = String(label).split(' & ');
        drawLabel(l1, cx, cy - 1, labelFontSize, 'bold', [0, 0, 0], 'center');
        drawLabel('& ' + l2, cx, cy + labelFontSize * 0.42 + 1.4, labelFontSize, 'bold', [0, 0, 0], 'center');
      } else {
        drawLabel(String(label), cx, cy + 1.2, labelFontSize, 'bold', [0, 0, 0], 'center');
      }
    }

    if (planets.length > 0) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(0, 0, 0);
      // A house with 3+ planets was overflowing the house's own boundary on
      // one line — split across 2 rows instead, same fix as the direction
      // labels above.
      const shortNames = planets.map((p) => GRAHA_SHORT[p] || p);
      if (shortNames.length > 2) {
        const mid = Math.ceil(shortNames.length / 2);
        doc.text(shortNames.slice(0, mid).join(' '), cx, cy + 6, { align: 'center' });
        doc.text(shortNames.slice(mid).join(' '), cx, cy + 9.8, { align: 'center' });
      } else {
        doc.text(shortNames.join(' '), cx, cy + 6, { align: 'center' });
      }
    }
  });

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.35);
  doc.rect(x, y, S, S, 'S');
}

// ── South Indian PDF drawer — jsPDF vector version of SouthIndianChartSVG ──
// Same fixed-Rashi-cell math as the live-editor SVG above (SOUTH_INDIAN_POSITIONS/
// SOUTH_LOOP_ORDER/the houseForRashi inverse formula) redrawn with jsPDF calls,
// so it can go into the exported PDF the same way drawNorthIndianChartPdf does.
export function drawSouthIndianChartPdf(doc, { x, y, size, lagnaRashi, houses = {}, houseLabels = null, labelFontSize = 8.5, drawText = null }) {
  const S = size;
  const drawLabel = drawText || defaultDrawText(doc);
  const CELL = S / 4;
  const cellXY = (row, col) => ({ cx: x + col * CELL, cy: y + row * CELL });
  const houseForRashi = (rashiNum) => lagnaRashi ? (((rashiNum - lagnaRashi + 12) % 12) + 1) : null;

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.25);

  SOUTH_LOOP_ORDER.forEach((rashiNum) => {
    const [row, col] = SOUTH_INDIAN_POSITIONS[rashiNum];
    const { cx: bx, cy: by } = cellXY(row, col);
    const houseNum = houseForRashi(rashiNum);
    const cx = bx + CELL / 2, cy = by + CELL / 2;

    doc.rect(bx, by, CELL, CELL, 'S');

    // Rashi's own fixed short-code — never moves regardless of Ascendant.
    doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5); doc.setTextColor(107, 114, 128);
    doc.text(RASHI_SHORT[rashiNum], bx + 1.5, by + 4);

    // Lagna marker — small diagonal in the corner, same convention as the SVG.
    if (houseNum === 1) {
      doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.2);
      doc.line(bx + CELL - 5.5, by + 0.6, bx + CELL - 0.6, by + 5.5);
    }

    // Rashi number (Aries 1 ... Pisces 12), bottom-right — same numbering as the North chart and the live SVG.
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(55, 65, 81);
    doc.text(String(rashiNum), bx + CELL - 1.8, by + CELL - 1.8, { align: 'right' });

    const label = houseLabels ? (houseNum ? houseLabels[houseNum] : null) : RASHI_SHORT[rashiNum];
    if (label) {
      if (String(label).includes(' & ')) {
        const [l1, l2] = String(label).split(' & ');
        drawLabel(l1, cx, cy - 0.5, labelFontSize, 'bold', [0, 0, 0], 'center');
        drawLabel('& ' + l2, cx, cy + labelFontSize * 0.42 + 1.6, labelFontSize, 'bold', [0, 0, 0], 'center');
      } else {
        drawLabel(String(label), cx, cy + 1, labelFontSize, 'bold', [0, 0, 0], 'center');
      }
    }

    const planets = houseNum ? (houses[houseNum] || []) : [];
    if (planets.length > 0) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(0, 0, 0);
      const shortNames = planets.map((p) => GRAHA_SHORT[p] || p);
      if (shortNames.length > 2) {
        const mid = Math.ceil(shortNames.length / 2);
        doc.text(shortNames.slice(0, mid).join(' '), cx, cy + 5, { align: 'center' });
        doc.text(shortNames.slice(mid).join(' '), cx, cy + 8, { align: 'center' });
      } else {
        doc.text(shortNames.join(' '), cx, cy + 5, { align: 'center' });
      }
    }
  });

  // Loop arrows — same small tail+arrowhead cue as the live editor, centred
  // on the shared border line between each pair of consecutive Rashi cells.
  const ARROW = size * 0.018, TAIL = size * 0.03;
  const DIR_VEC = { right: [1, 0], left: [-1, 0], down: [0, 1], up: [0, -1] };
  doc.setDrawColor(156, 163, 175); doc.setFillColor(156, 163, 175);
  SOUTH_LOOP_ORDER.forEach((rashiNum, i) => {
    const nextRashi = SOUTH_LOOP_ORDER[(i + 1) % SOUTH_LOOP_ORDER.length];
    const [r1, c1] = SOUTH_INDIAN_POSITIONS[rashiNum];
    const [r2, c2] = SOUTH_INDIAN_POSITIONS[nextRashi];
    const p1 = cellXY(r1, c1), p2 = cellXY(r2, c2);
    const mx = (p1.cx + p2.cx) / 2 + CELL / 2, my = (p1.cy + p2.cy) / 2 + CELL / 2;
    const dir = r1 === r2 ? (c2 > c1 ? 'right' : 'left') : (r2 > r1 ? 'down' : 'up');
    const [dx, dy] = DIR_VEC[dir];
    const headCx = mx + dx * (TAIL / 2), headCy = my + dy * (TAIL / 2);
    const half = TAIL / 2 + ARROW;
    doc.setLineWidth(0.35);
    doc.line(mx - dx * half, my - dy * half, headCx - dx * (ARROW - 0.4), headCy - dy * (ARROW - 0.4));
    const perpX = -dy, perpY = dx; // perpendicular unit vector, for the arrowhead's base width
    doc.triangle(
      headCx - dx * ARROW + perpX * ARROW, headCy - dy * ARROW + perpY * ARROW,
      headCx - dx * ARROW - perpX * ARROW, headCy - dy * ARROW - perpY * ARROW,
      headCx + dx * ARROW, headCy + dy * ARROW,
      'F'
    );
  });

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.35);
  doc.rect(x, y, S, S, 'S');
}
